<template>
  <section>
    <v-divider class="my-6" />
    <h5 class="wc-h5 mb-2">Submiembros del miembro titular</h5>
    <span>
      Agrega o edita submiembros/colaboradores asignados a este miembro.
      Los cambios se aplican de inmediato.
    </span>

    <v-row class="mt-2 mb-2" align="center">
      <v-col cols="12" md="9">
        <v-text-field
          v-model="search"
          filled
          dense
          rounded
          prepend-inner-icon="mdi-magnify"
          label="Buscar por nombre, apellido o correo"
          hide-details
          @keyup.enter="onSearch"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          filled
          dense
          rounded
          hide-details
          label="Estado"
          @change="onSearch"
        />
      </v-col>
    </v-row>
    <v-row class="mb-2" align="center" justify="end">
      <v-col cols="12" md="auto" class="d-flex flex-wrap justify-end">
        <v-btn
          elevation="0"
          class="white--text text-bold rounded-pill"
          :color="color.primaryVariant"
          :disabled="limitReached"
          @click="openCreate"
        >
          <v-icon class="mr-2">mdi-account-plus-outline</v-icon>
          Agregar submiembro
        </v-btn>
      </v-col>
    </v-row>

    <v-alert
      v-if="limitReached"
      type="warning"
      dense
      text
      class="mb-2"
    >
      Has alcanzado el límite de {{ limitValue }} submiembro(s) permitidos para este miembro.
    </v-alert>

    <v-alert
      v-if="errorMessage"
      type="error"
      dense
      text
      class="mb-2"
    >
      {{ errorMessage }}
    </v-alert>

    <div v-if="loading" class="d-flex align-center my-4">
      <v-progress-circular indeterminate size="20" width="2" class="mr-2" />
      <span>Cargando submiembros…</span>
    </div>

    <v-data-table
      v-else
      :headers="headers"
      :items="submembers"
      :no-data-text="
        search
          ? 'No se encontraron submiembros con el filtro aplicado.'
          : 'Este miembro aún no tiene submiembros asignados.'
      "
      class="elevation-0"
      hide-default-footer
    >
      <template v-slot:item.name="{ item }">
        <div class="d-flex flex-column">
          <span class="font-weight-medium">
            {{ fullName(item) }}
          </span>
          <span class="text-caption grey--text text--darken-1">
            {{ item.email }}
          </span>
        </div>
      </template>
      <template v-slot:item.position="{ item }">
        <span v-if="item.position">{{ item.position }}</span>
        <span v-else class="grey--text text--darken-1">—</span>
      </template>
      <template v-slot:item.validationStatus="{ item }">
        <v-chip
          small
          :color="statusColor(item.validationStatus).bg"
          :text-color="statusColor(item.validationStatus).text"
        >
          {{ statusColor(item.validationStatus).label }}
        </v-chip>
      </template>
      <template v-slot:item.createdAt="{ item }">
        <span v-if="item.createdAt">{{ formatDate(item.createdAt) }}</span>
        <span v-else class="grey--text text--darken-1">—</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          :color="color.primaryVariant"
          :title="'Editar submiembro'"
          @click="openEdit(item)"
        >
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <div
      v-if="totalDocs > 0"
      class="d-flex align-center justify-space-between mt-2 px-2"
    >
      <span class="text-caption grey--text text--darken-1">
        Mostrando {{ pageRangeStart }}–{{ pageRangeEnd }} de {{ totalDocs }}
        {{ totalDocs === 1 ? 'submiembro' : 'submiembros' }}
      </span>
      <div class="d-flex align-center">
        <v-btn
          icon
          small
          :disabled="!canGoPrev || loading"
          :title="'Página anterior'"
          @click="goToPrevPage"
        >
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
        <span class="mx-3 text-caption">
          Página <strong>{{ currentPage }}</strong> de {{ totalPages }}
        </span>
        <v-btn
          icon
          small
          :disabled="!canGoNext || loading"
          :title="'Página siguiente'"
          @click="goToNextPage"
        >
          <v-icon small>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="formDialog" max-width="520" persistent>
      <v-card color="white" class="pa-4">
        <div class="d-flex align-center mb-2">
          <h5 class="wc-h5 mb-0">
            {{ editingId ? 'Editar submiembro' : 'Agregar submiembro' }}
          </h5>
          <v-spacer />
          <v-btn icon small @click="closeForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <span class="grey--text text--darken-1">
          {{ editingId ? 'Actualiza la información del submiembro.' : 'Crea un submiembro y asígnalo a este miembro titular.' }}
        </span>
        <v-form v-model="formValid" class="mt-4">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.name"
                filled
                dense
                rounded
                label="Nombre(s)"
                :rules="[requiredRule]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.firstLastname"
                filled
                dense
                rounded
                label="Apellido paterno"
                :rules="[requiredRule]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.secondLastname"
                filled
                dense
                rounded
                label="Apellido materno"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.email"
                filled
                dense
                rounded
                label="Correo electrónico"
                type="email"
                :rules="[requiredRule, emailRule]"
                :disabled="!!editingId"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="form.position"
                filled
                dense
                rounded
                label="Cargo / puesto"
              />
            </v-col>
          </v-row>
        </v-form>
        <v-alert v-if="formError" type="error" dense text class="mb-2">
          {{ formError }}
        </v-alert>
        <div class="d-flex justify-end mt-2">
          <v-btn
            text
            class="mr-2"
            :disabled="saving"
            @click="closeForm"
          >
            Cancelar
          </v-btn>
          <v-btn
            elevation="0"
            class="white--text text-bold rounded-pill"
            :color="color.primaryVariant"
            :loading="saving"
            :disabled="!formValid || saving"
            @click="save"
          >
            <v-icon class="mr-2">mdi-content-save</v-icon>
            {{ editingId ? 'Guardar cambios' : 'Crear submiembro' }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { colors } from '@/constants/colors.js'

const STATUS_META = {
  ACTIVE: { label: 'Activo', bg: '#c8e6c9', text: '#1b5e20' },
  PROSPECTO: { label: 'Prospecto', bg: '#fff3cd', text: '#856404' },
  INACTIVO: { label: 'Inactivo', bg: '#e0e0e0', text: '#424242' },
  EXPIRED: { label: 'Expirado', bg: '#ffe0b2', text: '#e65100' },
  DELETED: { label: 'Eliminado', bg: '#ffcdd2', text: '#b71c1c' },
}

export default {
  name: 'AdminSubmembersSection',
  props: {
    memberId: {
      type: String,
      default: '',
    },
    limitValue: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      color: colors,
      search: '',
      statusFilter: '',
      currentPage: 1,
      formDialog: false,
      formValid: false,
      formError: null,
      editingId: null,
      form: {
        name: '',
        firstLastname: '',
        secondLastname: '',
        email: '',
        position: '',
      },
      saving: false,
      errorMessage: null,
    }
  },
  computed: {
    ...mapState('adminSubmembers', {
      submembers: (state) => state.submembers || [],
      totalSubmembers: (state) => state.totalSubmembers || 0,
      loading: (state) => state.loading,
      totalDocs: (state) => state.totalDocs,
      totalPages: (state) => state.totalPages,
      page: (state) => state.page,
    }),
    headers() {
      return [
        { text: 'Submiembro', value: 'name', sortable: false },
        { text: 'Cargo', value: 'position', sortable: false },
        { text: 'Estado', value: 'validationStatus', sortable: false },
        { text: 'Alta', value: 'createdAt', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'end' },
      ]
    },
    statusOptions() {
      return [
        { text: 'Todos los estados', value: '' },
        { text: 'Activo', value: 'ACTIVE' },
        { text: 'Pendiente', value: 'PROSPECTO' },
        { text: 'Inactivo', value: 'INACTIVO' },
        { text: 'Expirado', value: 'EXPIRED' },
      ]
    },
    limitReached() {
      return this.limitValue > 0 && this.totalSubmembers >= this.limitValue
    },
    canGoPrev() {
      return this.currentPage > 1
    },
    canGoNext() {
      return this.currentPage < this.totalPages
    },
    pageRangeStart() {
      if (!this.totalDocs) return 0
      return (this.currentPage - 1) * 10 + 1
    },
    pageRangeEnd() {
      if (!this.totalDocs) return 0
      return Math.min(this.currentPage * 10, this.totalDocs)
    },
  },
  watch: {
    memberId: {
      immediate: true,
      handler(next, previous) {
        if (!next) return
        if (next !== previous) {
          this.currentPage = 1
          this.refresh()
        } else if (!previous) {
          this.refresh()
        }
      },
    },
  },
  methods: {
    ...mapActions('adminSubmembers', [
      'fetchAdminSubmembers',
      'createAdminSubmember',
      'updateAdminSubmember',
    ]),
    ...mapMutations('notification', ['show']),
    requiredRule(v) {
      return (v !== null && v !== undefined && String(v).trim() !== '') || 'Este campo es obligatorio'
    },
    emailRule(v) {
      if (!v) return true
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Ingresa un correo válido'
    },
    fullName(item) {
      if (!item) return ''
      return [item.name, item.firstLastname, item.secondLastname]
        .filter(Boolean)
        .join(' ')
        .trim()
    },
    statusColor(status) {
      return (
        STATUS_META[status] || { label: status || 'N/D', bg: '#e0e0e0', text: '#424242' }
      )
    },
    formatDate(value) {
      try {
        return new Date(value).toLocaleDateString('es-MX', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      } catch (e) {
        return value
      }
    },
    refresh() {
      if (!this.memberId) return
      this.errorMessage = null
      this.currentPage = 1
      this.fetchPage(1)
    },
    fetchPage(page) {
      if (!this.memberId) return
      this.errorMessage = null
      this.fetchAdminSubmembers({
        memberId: this.memberId,
        query: {
          page,
          name: this.search.trim() || undefined,
          validationStatus: this.statusFilter || undefined,
        },
      })
        .then((res) => {
          if (!res || res.status === false) {
            this.errorMessage = res?.message || 'No fue posible cargar los submiembros'
            return
          }
          const data = res.data || {}
          const totalPages = Number(data.totalPages) || 0
          const returnedPage = Number(data.page) || page
          if (totalPages && returnedPage > totalPages) {
            this.currentPage = totalPages
            this.fetchPage(totalPages)
          } else {
            this.currentPage = returnedPage
          }
        })
    },
    onSearch() {
      this.currentPage = 1
      this.fetchPage(1)
    },
    goToPrevPage() {
      if (!this.canGoPrev || this.loading) return
      this.fetchPage(this.currentPage - 1)
    },
    goToNextPage() {
      if (!this.canGoNext || this.loading) return
      this.fetchPage(this.currentPage + 1)
    },
    openCreate() {
      this.editingId = null
      this.form = { name: '', firstLastname: '', secondLastname: '', email: '', position: '' }
      this.formError = null
      this.formDialog = true
    },
    openEdit(item) {
      this.editingId = item._id
      this.form = {
        name: item.name || '',
        firstLastname: item.firstLastname || '',
        secondLastname: item.secondLastname || '',
        email: item.email || '',
        position: item.position || '',
      }
      this.formError = null
      this.formDialog = true
    },
    closeForm() {
      this.formDialog = false
      this.editingId = null
      this.formError = null
    },
    save() {
      this.formError = null
      this.saving = true
      const payload = {
        name: this.form.name.trim(),
        firstLastname: this.form.firstLastname.trim(),
        secondLastname: this.form.secondLastname.trim(),
        email: this.form.email.trim().toLowerCase(),
        position: this.form.position.trim(),
      }
      const action = this.editingId
        ? this.updateAdminSubmember({
            memberId: this.memberId,
            submemberId: this.editingId,
            user: payload,
          })
        : this.createAdminSubmember({
            memberId: this.memberId,
            user: payload,
          })

      action
        .then((res) => {
          if (res?.status === false) {
            this.formError = res.message || 'No fue posible guardar el submiembro'
            this.show({
              text: this.formError,
              color: 'error',
            })
            return
          }
          this.show({
            text: this.editingId
              ? 'Submiembro actualizado correctamente'
              : 'Submiembro creado correctamente',
            color: 'success',
          })
          this.closeForm()
          this.refresh()
          this.$emit('updated')
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>
